import {
    Modal, RadioTile, TileGroup, Tooltip,
} from '@carbon/react';
import { Information } from '@carbon/icons-react';
import { Chip, Group } from '@mantine/core';
import { useEffect, useMemo, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import styles from './ConfirmationEditEventModal.module.scss';

import sharedMessages from 'Components/utils/sharedMessages';
import { ContextEvent } from '../../EventHandler/EntityEventContext';
import {
    EventRecurrenceDto,
    HowLongDto,
    HowLongMode,
    RecurrenceType,
    UpdateEvent,
} from 'api/dtos/event';
import {
    getAvailableWeekdays,
    sanitizeSelectedWeekdays,
} from '../selectedWeekdaysUtils';

type Props = {
    onFinalSubmit: (mode: UpdateEvent, selectedWeekdays?: number[]) => void;
    isOpen: boolean;
    onClose: () => void;
    values: ContextEvent;
    event?: ContextEvent;
};

export function areRecurrencesEqual(a: EventRecurrenceDto, b: EventRecurrenceDto): boolean {
    if (a.type !== b.type) return false;

    switch (a.type) {
    case RecurrenceType.None:
        return true;

    case RecurrenceType.Daily:
        return (
            (a.daily?.interval ?? 1) === (b.daily?.interval ?? 1) &&
            [...(a.daily?.excludedDays ?? [])].sort().join(',') ===
                [...(b.daily?.excludedDays ?? [])].sort().join(',')
        );

    case RecurrenceType.Weekly:
        if (a.weekly?.interval !== b.weekly?.interval) return false;

        const daysA = a.weekly?.days ?? [];
        const daysB = b.weekly?.days ?? [];
        if (daysA.length !== daysB.length) return false;

        return [...daysA].sort().every((v, i) => v === [...daysB].sort()[i]);

    case RecurrenceType.Monthly:
        return (
            a.monthly?.interval === b.monthly?.interval &&
        a.monthly?.dayOfMonth === b.monthly?.dayOfMonth
        );

    case RecurrenceType.Yearly:
        return (
            a.yearly?.month === b.yearly?.month &&
        a.yearly?.dayOfMonth === b.yearly?.dayOfMonth
        );

    default:
        return false;
    }
}

export function areHowLongSettingsEqual(
    a: HowLongDto | null | undefined,
    b: HowLongDto | null | undefined,
): boolean {
    const modeA = a?.mode ?? HowLongMode.Always;
    const modeB = b?.mode ?? HowLongMode.Always;

    if (modeA !== modeB) return false;

    if (modeA === HowLongMode.UntilDate) {
        return a?.untilDate === b?.untilDate;
    }

    if (modeA === HowLongMode.ForXTimes) {
        return a?.times === b?.times;
    }

    return true;
}

const ConfirmationEditEventModal = ({
    onFinalSubmit,
    isOpen,
    onClose,
    values,
    event,
}: Props) => {
    const intl = useIntl();
    const [currentValue, setCurrentValue] = useState<UpdateEvent>(UpdateEvent.Single);
    const [selectedWeekdays, setSelectedWeekdays] = useState<number[]>([]);

    const isRecurring = (event ? event.recurrence.type : RecurrenceType.None) !== RecurrenceType.None;
    const canSelectWeekdays = event?.recurrence.type === RecurrenceType.Daily;
    const availableWeekdays = useMemo(
        () => getAvailableWeekdays(event?.recurrence),
        [event?.recurrence],
    );
    const isSelectedWeekdaysMode = currentValue === UpdateEvent.SelectedWeekdays;
    const isWeekdaySelectionInvalid = isSelectedWeekdaysMode && selectedWeekdays.length === 0;
    const hasRecurrenceSettingsChanged = event ? (
        !areRecurrencesEqual(values.recurrence, event.recurrence) ||
        !areHowLongSettingsEqual(values.howLong, event.howLong)
    ) : false;

    useEffect(() => {
        if (isOpen) {
            setCurrentValue(UpdateEvent.Single);
            setSelectedWeekdays([]);
        }
    }, [event?.id, isOpen]);

    const onSubmit = () => {
        if (isWeekdaySelectionInvalid) {
            return;
        }

        onClose();
        const mode = hasRecurrenceSettingsChanged ? UpdateEvent.Series : currentValue;
        const sanitizedWeekdays = mode === UpdateEvent.SelectedWeekdays
            ? sanitizeSelectedWeekdays(selectedWeekdays, event?.recurrence)
            : undefined;

        onFinalSubmit(mode, sanitizedWeekdays);
    };

    const onChange = (value: unknown) => {
        setCurrentValue(value as UpdateEvent);
    };

    if (!isRecurring) {
        return null;
    }

    if (hasRecurrenceSettingsChanged) {
        return (
            <Modal
                open={isOpen}
                onRequestClose={onClose}
                onRequestSubmit={onSubmit}
                modalHeading={intl.formatMessage(messages.recurrenceChangeHeading)}
                primaryButtonText={intl.formatMessage(messages.updateSeries)}
                closeButtonLabel={intl.formatMessage(sharedMessages.cancel)}
                secondaryButtonText={intl.formatMessage(sharedMessages.back)}
            >
                <p>{intl.formatMessage(messages.recurrenceChangeDescription)}</p>
            </Modal>
        );
    }

    return (
        <Modal
            open={isOpen}
            onRequestClose={onClose}
            onRequestSubmit={onSubmit}
            modalHeading={intl.formatMessage(messages.heading)}
            primaryButtonText={intl.formatMessage(sharedMessages.update)}
            primaryButtonDisabled={isWeekdaySelectionInvalid}
            closeButtonLabel={intl.formatMessage(sharedMessages.cancel)}
            secondaryButtonText={intl.formatMessage(sharedMessages.back)}
        >
            <TileGroup
                defaultSelected={UpdateEvent.Single}
                legend={intl.formatMessage(messages.select)}
                valueSelected={currentValue}
                onChange={onChange}
                name="select events to change"
            >
                <RadioTile
                    id={UpdateEvent.Single.toString()}
                    className={styles.singleElement}
                    value={UpdateEvent.Single}
                >
                    {intl.formatMessage(messages.thisEvent)}
                </RadioTile>
                <RadioTile
                    id={UpdateEvent.Future.toString()}
                    className={styles.singleElement}
                    value={UpdateEvent.Future}
                >
                    {intl.formatMessage(messages.allFutureEvents)}
                </RadioTile>
                <RadioTile
                    id={UpdateEvent.Series.toString()}
                    className={canSelectWeekdays ? styles.singleElement : undefined}
                    value={UpdateEvent.Series}
                >
                    {intl.formatMessage(messages.allEvents)}
                </RadioTile>
                {canSelectWeekdays && (
                    <RadioTile
                        id={UpdateEvent.SelectedWeekdays}
                        value={UpdateEvent.SelectedWeekdays}
                    >
                        {intl.formatMessage(messages.selectedWeekdays)}
                    </RadioTile>
                )}
            </TileGroup>
            {isSelectedWeekdaysMode && (
                <div className={styles.weekdaySelection}>
                    <div className={styles.weekdaySelectionLabel} id="selected-weekdays-label">
                        <span>{intl.formatMessage(messages.selectWeekdays)}</span>
                        <Tooltip
                            label={intl.formatMessage(messages.availableWeekdaysTooltip)}
                            align="right"
                        >
                            <button
                                type="button"
                                className={styles.infoButton}
                                aria-label={intl.formatMessage(messages.availableWeekdaysTooltip)}
                            >
                                <Information size={16} aria-hidden="true" />
                            </button>
                        </Tooltip>
                    </div>
                    <div role="group" aria-labelledby="selected-weekdays-label">
                        <Chip.Group
                            multiple
                            value={selectedWeekdays.map(String)}
                            onChange={(weekdays) => {
                                setSelectedWeekdays(weekdays.map(Number));
                            }}
                        >
                            <Group gap="xs">
                                {availableWeekdays.map((day) => (
                                    <Chip
                                        key={day}
                                        value={String(day)}
                                        size="sm"
                                        variant="outline"
                                    >
                                        {intl.formatMessage(weekdayMessageList[day])}
                                    </Chip>
                                ))}
                            </Group>
                        </Chip.Group>
                    </div>
                    {isWeekdaySelectionInvalid && (
                        <p className={styles.validationMessage} role="alert">
                            {intl.formatMessage(messages.weekdaySelectionRequired)}
                        </p>
                    )}
                </div>
            )}
        </Modal>
    );
};

const messages = defineMessages({
    select: {
        id: 'src.Screens.Dashboard.Events.EditEventHandler.ConfirmationEditEventModal.ConfirmationEditEventModal.select',
        defaultMessage: 'Wybierz które wydarzenia zaktualizować',
    },
    heading: {
        id: 'src.Screens.Dashboard.Events.EditEventHandler.ConfirmationEditEventModal.ConfirmationEditEventModal.heading',
        defaultMessage: 'Zaktualizuj wydarzenie cykliczne',
    },
    thisEvent: {
        id: 'src.Screens.Dashboard.Events.EditEventHandler.ConfirmationEditEventModal.ConfirmationEditEventModal.thisEvent',
        defaultMessage: 'Tylko to wydarzenie',
    },
    allFutureEvents: {
        id: 'src.Screens.Dashboard.Events.EditEventHandler.ConfirmationEditEventModal.ConfirmationEditEventModal.allFutureEvents',
        defaultMessage: 'To i wszystkie przyszłe wydarzenia',
    },
    allEvents: {
        id: 'src.Screens.Dashboard.Events.EditEventHandler.ConfirmationEditEventModal.ConfirmationEditEventModal.allEvents',
        defaultMessage: 'Wszystkie wydarzenia w serii',
    },
    selectedWeekdays: {
        id: 'src.Screens.Dashboard.Events.EditEventHandler.ConfirmationEditEventModal.ConfirmationEditEventModal.selectedWeekdays',
        defaultMessage: 'W wybrane dni powtarzania',
    },
    selectWeekdays: {
        id: 'src.Screens.Dashboard.Events.EditEventHandler.ConfirmationEditEventModal.ConfirmationEditEventModal.selectWeekdays',
        defaultMessage: 'Dni powtarzania',
    },
    availableWeekdaysTooltip: {
        id: 'src.Screens.Dashboard.Events.EditEventHandler.ConfirmationEditEventModal.ConfirmationEditEventModal.availableWeekdaysTooltip',
        defaultMessage: 'Wyświetlane są tylko dni, w których to wydarzenie występuje. Pozostałe dni nie są dostępne, ponieważ zostały wykluczone z powtarzania tej serii.',
    },
    weekdaySelectionRequired: {
        id: 'src.Screens.Dashboard.Events.EditEventHandler.ConfirmationEditEventModal.ConfirmationEditEventModal.weekdaySelectionRequired',
        defaultMessage: 'Wybierz co najmniej jeden dzień powtarzania.',
    },
    recurrenceChangeHeading: {
        id: 'src.Screens.Dashboard.Events.EditEventHandler.ConfirmationEditEventModal.ConfirmationEditEventModal.recurrenceChangeHeading',
        defaultMessage: 'Zmiana powtarzania całej serii',
    },
    recurrenceChangeDescription: {
        id: 'src.Screens.Dashboard.Events.EditEventHandler.ConfirmationEditEventModal.ConfirmationEditEventModal.recurrenceChangeDescription',
        defaultMessage: 'Zmieniono powtarzanie lub jego ustawienia. Ta zmiana zostanie zastosowana do wszystkich wydarzeń w serii.',
    },
    updateSeries: {
        id: 'src.Screens.Dashboard.Events.EditEventHandler.ConfirmationEditEventModal.ConfirmationEditEventModal.updateSeries',
        defaultMessage: 'Zaktualizuj całą serię',
    },
});

const weekdayMessages = defineMessages({
    monday: {
        id: 'src.Screens.Dashboard.Events.EditEventHandler.ConfirmationEditEventModal.ConfirmationEditEventModal.monday',
        defaultMessage: 'Pn',
    },
    tuesday: {
        id: 'src.Screens.Dashboard.Events.EditEventHandler.ConfirmationEditEventModal.ConfirmationEditEventModal.tuesday',
        defaultMessage: 'Wt',
    },
    wednesday: {
        id: 'src.Screens.Dashboard.Events.EditEventHandler.ConfirmationEditEventModal.ConfirmationEditEventModal.wednesday',
        defaultMessage: 'Śr',
    },
    thursday: {
        id: 'src.Screens.Dashboard.Events.EditEventHandler.ConfirmationEditEventModal.ConfirmationEditEventModal.thursday',
        defaultMessage: 'Czw',
    },
    friday: {
        id: 'src.Screens.Dashboard.Events.EditEventHandler.ConfirmationEditEventModal.ConfirmationEditEventModal.friday',
        defaultMessage: 'Pt',
    },
    saturday: {
        id: 'src.Screens.Dashboard.Events.EditEventHandler.ConfirmationEditEventModal.ConfirmationEditEventModal.saturday',
        defaultMessage: 'Sob',
    },
    sunday: {
        id: 'src.Screens.Dashboard.Events.EditEventHandler.ConfirmationEditEventModal.ConfirmationEditEventModal.sunday',
        defaultMessage: 'Nd',
    },
});

const weekdayMessageList = [
    weekdayMessages.monday,
    weekdayMessages.tuesday,
    weekdayMessages.wednesday,
    weekdayMessages.thursday,
    weekdayMessages.friday,
    weekdayMessages.saturday,
    weekdayMessages.sunday,
];

export default ConfirmationEditEventModal;
